import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import NavigatorManageTable, {
  nameSort,
  OverflowNameList,
  SimpleActionsMenu,
} from './NavigatorManageTable';

// Thin wrapper over the shared NavigatorManageTable — supplies only the
// program-tag-specific columns, cells, and bulk bar. Mirrors ManageProgramsTable.

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

const Swatch = styled.span`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #D6D6D6;
  background: ${(props) => props.color};
`;

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
`;

const BULK_ACTIONS = [
  {
    key: 'deleteTags', label: 'Delete', icon: 'delete', iconColor: '#FF0505',
  },
];

const NameCell = ({ row }) => {
  const { name, color } = row.original;
  return (
    <NameWrapper>
      <Swatch color={color || '#FFFFFF'} />
      <NameText title={name}>{name || 'Untitled tag'}</NameText>
    </NameWrapper>
  );
};

NameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      name: PropTypes.string,
      color: PropTypes.string,
    }),
  }).isRequired,
};

const ProgramsCell = ({ row }) => {
  const { programs, id } = row.original;
  const items = (programs || []).map((p) => ({ id: p.id, name: p.programName }));
  return <OverflowNameList items={items} idPrefix='program-tag-programs' rowId={id} variant='pill' />;
};

ProgramsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      programs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        programName: PropTypes.string,
      })),
    }),
  }).isRequired,
};

const ActionsCell = ({ row }) => {
  const t = row.original;
  return <SimpleActionsMenu onEdit={() => t.onEdit(t)} onDelete={() => t.onDelete(t)} />;
};

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onEdit: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const COLUMNS = [
  {
    Header: 'Tag',
    accessor: 'name',
    sortType: nameSort,
    headerStyle: { minWidth: '280px', flex: '1' },
    cellStyle: { minWidth: '280px', flex: '1' },
    Cell: NameCell,
  },
  {
    Header: 'Programs',
    id: 'programs',
    accessor: (row) => (row.programs || []).map((p) => p.programName).join(' '),
    disableSortBy: true,
    headerStyle: { width: '380px' },
    cellStyle: { width: '380px' },
    Cell: ProgramsCell,
  },
  {
    Header: 'Actions',
    id: 'actions',
    sticky: 'right',
    disableSortBy: true,
    headerStyle: { width: '90px', textAlign: 'center' },
    cellStyle: { width: '90px', textAlign: 'center' },
    Cell: ActionsCell,
  },
];

const ManageProgramTagsTable = ({
  tags, isLoading, hasError, onRetry, onEditTag, onDeleteTag, onBulkDelete, clearSelectionToken,
}) => {
  const tableData = React.useMemo(
    () => (tags || []).map((t) => ({
      ...t,
      onEdit: onEditTag,
      onDelete: onDeleteTag,
    })),
    [tags, onEditTag, onDeleteTag],
  );

  const handleBulkAction = (key, selectedIds) => {
    if (key === 'deleteTags') onBulkDelete(selectedIds);
  };

  return (
    <NavigatorManageTable
      data={tableData}
      columns={COLUMNS}
      isLoading={isLoading}
      hasError={hasError}
      errorMessage='Something went wrong loading tags.'
      onRetry={onRetry}
      searchKeys={['name']}
      searchPlaceholder='Search tags'
      emptyMessage='No program tags yet.'
      searchEmptyMessage='No tags match your search.'
      entityLabel='tag'
      defaultSortBy={[{ id: 'name', desc: false }]}
      bulkActions={BULK_ACTIONS}
      onBulkAction={handleBulkAction}
      clearSelectionToken={clearSelectionToken}
      virtualizeRows
    />
  );
};

ManageProgramTagsTable.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    color: PropTypes.string,
    programs: PropTypes.arrayOf(PropTypes.shape({})),
  })),
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  onRetry: PropTypes.func,
  onEditTag: PropTypes.func,
  onDeleteTag: PropTypes.func,
  onBulkDelete: PropTypes.func,
  clearSelectionToken: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

ManageProgramTagsTable.defaultProps = {
  tags: [],
  isLoading: false,
  hasError: false,
  onRetry: () => {},
  onEditTag: () => {},
  onDeleteTag: () => {},
  onBulkDelete: () => {},
  clearSelectionToken: 0,
};

export default ManageProgramTagsTable;
